"use client"
import {
  FieldError,
  FieldValues,
  FormProvider,
  useForm,
  useFormContext,
} from "react-hook-form"
import { Button } from "@/components/atoms"
import { zodResolver } from "@hookform/resolvers/zod"
import { LabeledInput } from "@/components/cells"
import { registerFormSchema, RegisterFormData } from "./schema"
import { sendVerifyCode, signup } from "@/lib/data/customer"
import { useEffect, useState } from "react"
import { cn } from "@/lib/utils"
import { useTranslate, useTolgee } from "@tolgee/react"
import { Container } from "@medusajs/ui"
import LocalizedClientLink from "@/components/molecules/LocalizedLink/LocalizedLink"
import { PasswordValidator } from "@/components/cells/PasswordValidator/PasswordValidator"
import { useAppContext } from "@/lib/context/app-context"
import { umamiTrackEvent } from "@/lib/utils/umami"
import { useParams, useRouter } from "next/navigation"

export const RegisterForm = () => {
  const methods = useForm<RegisterFormData>({
    resolver: zodResolver(registerFormSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      phone: "",
      emailCode: "",
      email: "",
      password: "",
    },
  })

  return (
    <FormProvider {...methods}>
      <Form />
    </FormProvider>
  )
}

const Form = () => {
  const { t } = useTranslate()
  const tolgee = useTolgee(["language"])
  const [passwordError, setPasswordError] = useState({
    isValid: false,
    lower: false,
    upper: false,
    "8chars": false,
    symbolOrDigit: false,
  })
  const [error, setError] = useState()
  let router = useRouter()
  let params = useParams()
  const {
    handleSubmit,
    register,
    watch,
    formState: { errors, isSubmitting },
    getValues,
    trigger
  } = useFormContext()

  let [limitTime, setLimitTime] = useState(0)


  useEffect(() => {
    const timer = setInterval(() => {
      setLimitTime(limitTime - 1)
      if (limitTime <= 0) {
        clearInterval(timer)
      }
    }, 1000)
    return () => clearInterval(timer);
  }, [limitTime]);


  let appContext = useAppContext()

  const submit = async (data: FieldValues) => {
    if (!passwordError.isValid) {
      return
    }

    const formData = new FormData()
    formData.append("email", data.email)
    formData.append("emailCode", data.emailCode)
    formData.append("password", data.password)
    formData.append("first_name", data.firstName)
    formData.append("last_name", data.lastName)
    formData.append("phone", data.phone)

    const res = await signup(formData)

    if (!res || typeof res === "string" || !res.id) {
      setError(typeof res === "string" ? res : undefined)
      return
    }

    appContext.analytics?.("sign_up", {
      method: "Email"
    })
    umamiTrackEvent("sign_up", appContext, {
      method: "Email"
    })

    router.replace(`/${params.locale}/user`)
  }

  const sendVerifyCodeSubmit = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    let is = await trigger(["email"])
    if (is) {
      setLimitTime(30);
      let rs = await sendVerifyCode(getValues().email, tolgee.getLanguage() ?? "en")
      if (rs) {
        setError(rs)
      }
    }
  }

  return (
    <main className="flex items-start justify-center py-[48px] px-[120px]">
      <div className="w-[510px] max-w-full border border-[#e5e7eb] rounded-[14px] overflow-hidden">
        {/* 上半部分 - 注册表单 */}
        <div className="bg-white px-[40px] py-[40px]">
          <h1 className="text-[24px] font-semibold text-[#101828] text-center leading-[32px] mb-[28px]">
            {t("register_form.heading")}
          </h1>
          <form onSubmit={handleSubmit(submit)}>
            <div className="flex flex-col md:flex-row gap-[16px] mb-[16px]">
              <LabeledInput
                className="md:w-1/2"
                label={t("register_form.first_name_label")}
                placeholder={t("register_form.first_name_placeholder")}
                error={errors.firstName as FieldError}
                {...register("firstName")}
              />
              <LabeledInput
                className="md:w-1/2"
                label={t("register_form.last_name_label")}
                placeholder={t("register_form.last_name_placeholder")}
                error={errors.lastName as FieldError}
                {...register("lastName")}
              />
            </div>
            <div className="flex flex-col md:flex-row gap-[16px] mb-[16px]">
              <LabeledInput
                className="md:w-1/2"
                label={t("register_form.email_label")}
                placeholder={t("register_form.email_placeholder")}
                error={errors.email as FieldError}
                {...register("email")}
              />
              <LabeledInput
                className="md:w-1/2"
                label={t("register_form.phone_label")}
                placeholder={t("register_form.phone_placeholder")}
                error={errors.phone as FieldError}
                {...register("phone")}
              />
            </div>
            <div className="mb-[16px]">
              <LabeledInput
                className="w-full"
                label={t("register_form.email_code_label")}
                placeholder={t("register_form.email_code_placeholder")}
                error={errors.emailCode as FieldError}
                {...register("emailCode")}
              >
                <button
                  onClick={sendVerifyCodeSubmit}
                  disabled={limitTime > 0}
                  className="h-[42px] px-[21px] border border-[#d1d5dc] rounded-[8px] bg-white text-[14px] font-medium text-[#364153] leading-[20px] tracking-[1.2496px] hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {limitTime > 0 ? `${t("register_form.send_button")}(${limitTime})` : t("register_form.send_button")}
                </button>
            </LabeledInput>
          </div>
          <div>
            <LabeledInput
              className=""
              label={t("register_form.password_label")}
              placeholder={t("register_form.password_placeholder")}
              type="password"
              error={errors.password as FieldError}
              {...register("password")}
            />
            <PasswordValidator
              password={watch("password")}
              setError={setPasswordError}
            />
          </div>
          {/* <LabeledInput
            label={t("register_form.confirm_password_label")}
            placeholder={t("register_form.confirm_password_placeholder")}
            type="password"
            error={errors.confirmPassword as FieldError}
            {...register("confirmPassword")}
          />
          <LabeledInput
            label={t("register_form.phone_label")}
            placeholder={t("register_form.phone_placeholder")}
            error={errors.phone as FieldError}
            {...register("phone")}
          /> */}
          {error && <p className="label-md text-negative">{error}</p>}

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full h-[44px] bg-[#101828] text-white text-[14px] font-medium leading-[20px] rounded-[8px] hover:bg-[#2a3340] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t("register_form.register_button")}
          </button>
        </form>
      </div>

      {/* 下半部分 - 登录链接 */}
      <div className="bg-[#f9fafb] border-t border-[#e5e7eb] px-[40px] py-[28px]">
        <p className="text-[16px] font-medium text-[#1e2939] leading-[24px] mb-[16px]">
          {t("register_form.already_have_account")}
        </p>
        <LocalizedClientLink href="/user">
          <button className="w-full h-[42px] bg-white border border-[#d1d5dc] rounded-[8px] text-[14px] font-medium text-[#364153] leading-[20px] hover:bg-gray-50">
            {t("register_form.sign_in")}
          </button>
        </LocalizedClientLink>
      </div>
    </div>
  </main>
  )
}
